import { useEffect, useState } from 'react';
import { i18n, wordCount } from '@wms/shared';
import { api } from '../lib/api.js';
import { Dialog } from './Dialog.js';
import { NumberInput } from './NumberInput.js';

interface Options {
  partnerId: string;
  skus: {
    id: string;
    code: string;
    nameLt: string;
    type: string;
    shelfQty: number;
    sources: { palletId: string; code: string; qty: number }[];
  }[];
}
export function PalletTopupDialog({
  palletId,
  onClose,
  onSaved,
}: {
  palletId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = i18n;
  const [options, setOptions] = useState<Options | null>(null);
  const [skuId, setSkuId] = useState('');
  const [source, setSource] = useState('SHELF');
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => {
    api<Options>(`/pallets/${palletId}/topup-options`)
      .then(setOptions)
      .catch(() => setError(true));
  }, [palletId]);
  const sku = options?.skus.find((s) => s.id === skuId);
  const max =
    source === 'RECEIPT'
      ? 1_000_000
      : source === 'SHELF'
        ? (sku?.shelfQty ?? 0)
        : (sku?.sources.find((s) => s.palletId === source)?.qty ?? 0);
  const valid =
    Boolean(sku) &&
    Number.isInteger(qty) &&
    qty > 0 &&
    qty <= max &&
    reason.trim().length > 0 &&
    (source !== 'RECEIPT' || wordCount(reason) >= 2);
  async function save() {
    if (!valid || !options || busy) return;
    setBusy(true);
    setError(false);
    try {
      if (source === 'RECEIPT')
        await api(`/partners/${options.partnerId}/stock/restock`, {
          method: 'POST',
          body: { palletId, lines: [{ skuId, qty, comment: reason }] },
        });
      else if (source === 'SHELF')
        await api('/pallets/from-shelf', {
          method: 'POST',
          body: {
            partnerId: options.partnerId,
            skuId,
            qty,
            reason,
            target: { type: 'EXISTING', palletId },
          },
        });
      else
        await api(`/pallets/${palletId}/transfer-content`, {
          method: 'POST',
          body: { sourcePalletId: source, skuId, qty, reason },
        });
      onSaved();
      onClose();
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog title={t('pallet.topup.title')} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <label className="block">
          {t('sku.code')}
          <select
            aria-label={t('sku.code')}
            value={skuId}
            onChange={(e) => {
              setSkuId(e.target.value);
              setSource('SHELF');
            }}
            className="w-full rounded border border-border bg-background p-2"
          >
            <option value="">—</option>
            {options?.skus.map((s) => (
              <option key={s.id} value={s.id}>
                {s.code} — {s.nameLt} ({t(`sku.type.${s.type}`)})
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          {t('pallet.topup.source')}
          <select
            aria-label={t('pallet.topup.source')}
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="w-full rounded border border-border bg-background p-2"
          >
            <option value="SHELF">
              {t('pallet.topup.shelf')} ({sku?.shelfQty ?? 0})
            </option>
            {sku?.sources.map((s) => (
              <option key={s.palletId} value={s.palletId}>
                {s.code} ({s.qty})
              </option>
            ))}
            <option value="RECEIPT">{t('pallet.topup.receipt')}</option>
          </select>
        </label>
        <p className="text-xs text-muted-foreground">
          {t(source === 'RECEIPT' ? 'pallet.topup.receipt_hint' : 'pallet.topup.transfer_hint')}
        </p>
        <label className="block">
          {t('pallet.shelf.qty')}
          <NumberInput
            integer
            min={1}
            max={max}
            value={qty}
            onChange={(v) => setQty(v ?? 0)}
            className="w-full rounded border border-border p-2"
          />
        </label>
        <label className="block">
          {t('stock.restock.comment')}
          <textarea
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full rounded border border-border bg-background p-2"
          />
        </label>
        {source === 'RECEIPT' ? <p className="text-xs">{t('stock.restock.comment_hint')}</p> : null}
        {error ? (
          <p role="alert" className="text-destructive">
            {t('common.error')}
          </p>
        ) : null}
        <button
          type="button"
          disabled={!valid || busy}
          onClick={() => void save()}
          className="rounded border border-border px-3 py-2 disabled:opacity-50"
        >
          {t('common.save')}
        </button>
      </div>
    </Dialog>
  );
}
